import { Input } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDebounce from "../../../../hooks/useDebounce";
import { KEYWORD_DEBOUNCE_MS } from "./ultis";

interface KeywordSearchInputProps
{
      /** The keyword currently filtering the list. */
      value: string;
      /** Called only when the keyword actually changes — never per keystroke. */
      onSearch: (keyword: string) => void;
      disabled?: boolean;
}

/**
 * One box matching an order number or an order name, shared by both order lists.
 *
 * Typing is kept local: the list is only re-queried once typing has stopped for
 * KEYWORD_DEBOUNCE_MS, or immediately on Enter, so half-typed order codes never
 * reach the endpoint.
 */
const KeywordSearchInput = ({
      value,
      onSearch,
      disabled,
}: KeywordSearchInputProps) =>
{
      const { t } = useTranslation();

      const [typed, setTyped] = useState(value);

      // The debounced callback is built when `typed` changes, so it closes over
      // whatever `value` was back then. Searching on Enter and then letting that
      // stale callback fire would repeat the same request — the guard below reads
      // the keyword through a ref so it always sees the current one.
      const committed = useRef(value);

      // The filter can also change from outside the box — Reset, or a back/forward
      // step through the URL — and the box has to follow it.
      useEffect(() =>
      {
            committed.current = value;
            setTyped(value);
      }, [value]);

      const commit = (next?: string) =>
      {
            const keyword = (next ?? typed).trim();
            if (keyword === committed.current) return;

            committed.current = keyword;
            onSearch(keyword);
      };

      useDebounce(() => commit(), [typed], KEYWORD_DEBOUNCE_MS);

      return (
            <Input
                  allowClear
                  disabled={disabled}
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  // Enter doesn't wait the debounce out, and emptying the box
                  // shouldn't either — a cleared filter is never a half-typed one.
                  onPressEnter={() => commit()}
                  onClear={() => commit("")}
                  placeholder={t("Enter order number or name")}
                  className="div-dropdown"
            />
      );
};

export default KeywordSearchInput;
